import { useState, useEffect } from 'react';
import { ZodType, z } from 'zod';
type EventEmitter = {
  events: Record<string, Function[]>;
  dispatch: (event: string, data: any) => void;
  subscribe: (event: string, callback: Function) => () => void;
};

// Event emitter for internal communication between hook instances
const eventEmitter: EventEmitter = {
  events: {},
  dispatch(event, data) {
    if (!this.events[event]) return;
    this.events[event]!.forEach(callback => callback(data));
  },
  subscribe(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event]!.push(callback);
    return () => {
      this.events[event] = this.events[event]!.filter(cb => cb !== callback);
    };
  }
};
export const useLocalStorage = <T extends ZodType<any, any>,>(params: {
  key: string;
  schema: T;
}) => {
  const {
    key,
    schema
  } = params;
  const readValue = (): z.infer<T> | undefined => {
    if (typeof window === 'undefined') return undefined;
    try {
      const item = window.localStorage.getItem(key);
      return item ? schema.parse(JSON.parse(item)) : undefined;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return undefined;
    }
  };
  const [storedValue, setStoredValue] = useState<z.infer<T> | undefined>(readValue);
  const setValue = (value: z.infer<T>) => {
    if (typeof window === 'undefined') return;
    try {
      if (value == null) window.localStorage.removeItem(key);else window.localStorage.setItem(key, JSON.stringify(schema.parse(value)));
      setStoredValue(value);
      eventEmitter.dispatch(key, value);
    } catch (error_0) {
      console.warn(`Error setting localStorage key “${key}”:`, error_0);
    }
  };
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        setStoredValue(readValue());
      }
    };
    const handleCustomEvent = (value_0: z.infer<T>) => {
      setStoredValue(value_0);
    };
    window.addEventListener('storage', handleStorageChange);
    const unsubscribe = eventEmitter.subscribe(key, handleCustomEvent);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      unsubscribe();
    };
  }, [key]);
  return [storedValue, setValue] as const;
};